import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { CURRENCIES, type Currency, type PlanningFxRate } from "../../entities/ledger/types";
import { humanError } from "../../shared/api/client";
import { Modal } from "../../shared/ui/Modal";
import { Select } from "../../shared/ui/Select";
import { deletePlanningFx, setPlanningFx } from "./api";
import { EmptyState } from "../../shared/ui/EmptyState";

// Planning FX are mutable assumptions for future values. Historical postings
// keep their frozen rates; changing a planning rate never rewrites history.
export function PlanningFxPanel({ bookID, baseCurrency, reportingCurrency, rates, onNotify }: { bookID: string; baseCurrency: Currency; reportingCurrency: Currency; rates: PlanningFxRate[]; onNotify: (message: string) => void }) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["planning"] });
  const [adding, setAdding] = useState(false);
  const [refused, setRefused] = useState("");
  const [form, setForm] = useState({ base: (baseCurrency === "EUR" ? "USD" : "EUR") as Currency, quote: baseCurrency, rate: "" });
  const save = useMutation({
    mutationFn: () => setPlanningFx(bookID, form.base, form.quote, form.rate.trim().replace(/\s/g, "").replace(",", ".")),
    onSuccess: async () => { setForm({ ...form, rate: "" }); setAdding(false); await refresh(); onNotify(`Planning rate ${form.base}/${form.quote} saved.`); },
  });
  const remove = useMutation({
    mutationFn: (rate: PlanningFxRate) => deletePlanningFx(bookID, rate.baseCurrency, rate.quoteCurrency),
    onSuccess: async () => { await refresh(); onNotify("Planning rate removed."); },
  });
  const missingReporting = reportingCurrency !== baseCurrency && !rates.some((rate) => (rate.baseCurrency === baseCurrency && rate.quoteCurrency === reportingCurrency) || (rate.baseCurrency === reportingCurrency && rate.quoteCurrency === baseCurrency));
  return (
    <section className="panel planning-panel fx-panel" aria-labelledby="fx-title">
      <div className="panel-heading">
        <div><p className="eyebrow">Planning FX</p><h2 id="fx-title">Rate assumptions</h2></div>
        <button className="primary-button compact" type="button" onClick={() => setAdding(true)}><span>Add a rate</span><span aria-hidden="true">＋</span></button>
      </div>
      <p className="panel-note">One rate per pair, usable in both directions, applied to plans, budgets and the forecast only. Actual transactions keep the rates frozen when they were recorded.{missingReporting ? ` Add ${baseCurrency}/${reportingCurrency} to value plans in the reporting currency.` : ""}</p>
      <ul className="fx-list">
        {rates.map((rate) => (
          <li key={`${rate.baseCurrency}/${rate.quoteCurrency}`}>
            <span><strong>1 {rate.baseCurrency} = {rate.rate} {rate.quoteCurrency}</strong><small>{rate.note || `updated ${new Date(rate.updatedAt).toLocaleDateString("en", { day: "numeric", month: "short" })}`}</small></span>
            <button className="chip danger" type="button" disabled={remove.isPending} onClick={() => remove.mutate(rate)} title="Remove this planning rate">Remove</button>
          </li>
        ))}
        {rates.length === 0 ? <li><EmptyState>No planning rates yet. A rate lets a plan in another currency be compared with this book&apos;s own.</EmptyState></li> : null}
      </ul>
      <Modal
        open={adding} onClose={() => setAdding(false)} title="Set a planning rate"
        subtitle="One rate per pair, usable in both directions. It values plans and budgets only, never recorded transactions."
        footer={<>
          <button className="secondary-button" type="button" onClick={() => setAdding(false)}>Cancel</button>
          <button className="primary-button compact" type="submit" form="fx-form" disabled={save.isPending || form.base === form.quote}>
            <span>{save.isPending ? "Saving…" : "Set rate"}</span><span aria-hidden="true">→</span>
          </button>
        </>}
      >
        <form id="fx-form" className="stacked-form" aria-label="Set planning rate"
          onSubmit={(event) => {
            event.preventDefault();
            if (!/^\d+(?:[.,]\d{1,8})?$/.test(form.rate.trim().replace(/\s/g, ""))) {
              setRefused("A rate is a positive number with at most eight decimals.");
              return;
            }
            setRefused("");
            save.mutate();
          }}>
          <div className="fx-row">
            <span className="fx-unit">1</span>
            <Select tone="compact" label="Base currency" value={form.base}
              onChange={(value) => setForm({ ...form, base: value as Currency })}
              options={CURRENCIES.map((value) => ({ value, label: value }))} />
            <span className="fx-unit">=</span>
            <span className="amount-input"><input value={form.rate} onChange={(event) => setForm({ ...form, rate: event.target.value })} inputMode="decimal" placeholder="3.026" required autoFocus /></span>
            <Select tone="compact" label="Quote currency" value={form.quote}
              onChange={(value) => setForm({ ...form, quote: value as Currency })}
              options={CURRENCIES.map((value) => ({ value, label: value }))} />
          </div>
          {form.base === form.quote ? <p className="form-error" role="alert">A rate needs two different currencies.</p> : null}
          {refused ? <p className="form-error" role="alert">{refused}</p> : null}
          {save.error ? <p className="form-error" role="alert">{humanError(save.error)}</p> : null}
        </form>
      </Modal>
      {save.error || remove.error ? <p className="form-error" role="alert">{humanError(save.error ?? remove.error)}</p> : null}
    </section>
  );
}
